"use client";

import { useCallback, useEffect, useRef, useState } from "react";

type UploadResult = {
  id: string;
  url: string;
  slug?: string;
  title?: string;
};

type SavedProject = {
  id: string;
  title: string;
  slug: string;
  url: string;
  createdAt: string;
};

function zipBaseName(fileName: string): string {
  return fileName.replace(/\.zip$/i, "").trim();
}

export function UploadPanel() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState("");
  const [titleTouched, setTitleTouched] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [projects, setProjects] = useState<SavedProject[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadProjects = useCallback(async () => {
    try {
      const res = await fetch("/api/projects", { cache: "no-store" });
      const data = await res.json();
      if (res.ok && Array.isArray(data.projects)) {
        setProjects(data.projects as SavedProject[]);
      }
    } catch {
      // keep existing list on error
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  const uploadFile = useCallback(
    async (file: File) => {
      setError(null);
      setResult(null);
      setCopied(false);

      if (!file.name.toLowerCase().endsWith(".zip")) {
        setError("Please upload a .zip file.");
        return;
      }

      setIsUploading(true);

      try {
        const formData = new FormData();
        formData.append("file", file);
        const resolvedTitle = title.trim() || zipBaseName(file.name);
        formData.append("title", resolvedTitle);

        const response = await fetch("/api/upload", {
          method: "POST",
          body: formData,
        });

        const data = await response.json();

        if (!response.ok) {
          setError(data.error ?? "Upload failed.");
          return;
        }

        setResult(data as UploadResult);
        await loadProjects();
      } catch {
        setError("Network error. Please try again.");
      } finally {
        setIsUploading(false);
      }
    },
    [title, loadProjects]
  );

  const onFileSelected = useCallback(
    (files: FileList | null) => {
      if (!files?.length) return;
      const file = files[0];
      if (!titleTouched && !title.trim()) {
        setTitle(zipBaseName(file.name));
      }
      void uploadFile(file);
    },
    [title, titleTouched, uploadFile]
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      onFileSelected(e.dataTransfer.files);
    },
    [onFileSelected]
  );

  const copyText = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Could not copy to clipboard.");
    }
  }, []);

  const copyLink = useCallback(() => {
    if (result?.url) void copyText(result.url);
  }, [result, copyText]);

  const deleteProject = useCallback(
    async (project: SavedProject) => {
      const confirmed = window.confirm(
        `Delete "${project.title}"? This cannot be undone.`
      );
      if (!confirmed) return;

      setDeletingId(project.id);
      setError(null);

      try {
        const res = await fetch(`/api/projects/${project.id}`, {
          method: "DELETE",
        });
        const data = await res.json();

        if (!res.ok) {
          setError(data.error ?? "Delete failed.");
          return;
        }

        if (result?.id === project.id) {
          setResult(null);
        }
        await loadProjects();
      } catch {
        setError("Network error. Could not delete project.");
      } finally {
        setDeletingId(null);
      }
    },
    [result, loadProjects]
  );

  return (
    <div className="mx-auto w-full max-w-lg py-12">
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
          Upload a project
        </h1>
        <p className="mt-2 text-zinc-600 dark:text-zinc-400">
          Upload a ZIP and get a permanent shareable link.
        </p>
        <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
          Developers can also push HTML folders to{" "}
          <span className="font-mono">gitlab-projects/</span> on GitLab, then
          run <span className="font-mono">npm run sync:gitlab-projects</span>.
        </p>
      </div>

      <div className="mb-4">
        <label
          htmlFor="project-title"
          className="block text-sm font-medium text-zinc-800 dark:text-zinc-200"
        >
          Project title
        </label>
        <input
          id="project-title"
          type="text"
          value={title}
          onChange={(e) => {
            setTitleTouched(true);
            setTitle(e.target.value);
          }}
          placeholder="Leave empty to use ZIP file name"
          className="mt-1 w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-50"
        />
        <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
          URL uses an English slug from the title (e.g.{" "}
          <span className="font-mono">خربش</span> →{" "}
          <span className="font-mono">/p/khrbsh</span>). Empty title uses the
          ZIP file name.
        </p>
      </div>

      <div
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-2xl border-2 border-dashed p-12 text-center transition-colors ${
          isDragging
            ? "border-blue-500 bg-blue-50 dark:bg-blue-950/30"
            : "border-zinc-300 bg-white hover:border-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:border-zinc-500"
        } ${isUploading ? "pointer-events-none opacity-60" : ""}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".zip,application/zip"
          className="hidden"
          onChange={(e) => onFileSelected(e.target.files)}
        />
        <p className="text-lg font-medium text-zinc-800 dark:text-zinc-200">
          {isUploading ? "Uploading…" : "Drop your ZIP here"}
        </p>
        <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
          or click to browse (max 25 MB)
        </p>
      </div>

      {error && (
        <p
          role="alert"
          className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300"
        >
          {error}
        </p>
      )}

      {result && (
        <div className="mt-6 rounded-2xl border border-zinc-200 bg-white p-6 dark:border-zinc-800 dark:bg-zinc-900">
          <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
            Your site is live — {result.title}
          </p>
          <a
            href={result.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 block break-all text-blue-600 hover:underline dark:text-blue-400"
          >
            {result.url}
          </a>
          <div className="mt-4 flex gap-3">
            <button
              type="button"
              onClick={copyLink}
              className="rounded-lg bg-zinc-900 px-4 py-2 text-sm font-medium text-white hover:bg-zinc-700 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-zinc-300"
            >
              {copied ? "Copied!" : "Copy link"}
            </button>
            <a
              href={result.url}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-lg border border-zinc-300 px-4 py-2 text-sm font-medium text-zinc-800 hover:bg-zinc-50 dark:border-zinc-600 dark:text-zinc-200 dark:hover:bg-zinc-800"
            >
              Open site
            </a>
          </div>
        </div>
      )}

      <section className="mt-10">
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-50">
          Manage projects
        </h2>
        <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
          Delete projects you no longer need. Browse all projects on the home
          page.
        </p>

        {loadingProjects ? (
          <p className="mt-4 text-sm text-zinc-500">Loading…</p>
        ) : projects.length === 0 ? (
          <p className="mt-4 text-sm text-zinc-500 dark:text-zinc-400">
            No uploads yet.
          </p>
        ) : (
          <ul className="mt-4 space-y-3">
            {projects.map((p) => (
              <li
                key={p.id}
                className="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900"
              >
                <p className="font-medium text-zinc-900 dark:text-zinc-100">
                  {p.title}
                </p>
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 block break-all text-sm text-blue-600 hover:underline dark:text-blue-400"
                >
                  {p.url}
                </a>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => void copyText(p.url)}
                    className="rounded-md border border-zinc-300 px-3 py-1 text-xs font-medium dark:border-zinc-600"
                  >
                    Copy
                  </button>
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-md border border-zinc-300 px-3 py-1 text-xs font-medium dark:border-zinc-600"
                  >
                    Open
                  </a>
                  <button
                    type="button"
                    disabled={deletingId === p.id}
                    onClick={() => void deleteProject(p)}
                    className="rounded-md border border-red-300 px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-900 dark:text-red-400 dark:hover:bg-red-950/40"
                  >
                    {deletingId === p.id ? "Deleting…" : "Delete"}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
